import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useSupabase } from '@/components/providers/SupabaseProvider';
import { getAuthToken } from '@/lib/supabase';

/**
 * Hook that returns true only when both Clerk user and Supabase auth are ready.
 * Use this in React Query hooks to prevent fetching with anon client before JWT is set.
 *
 * IMPORTANT: Checks actual JWT token existence, not just loading flags.
 * This prevents race conditions where isLoading=false but token isn't set yet.
 *
 * Returns:
 * - userId: string | undefined - Clerk user ID (only when auth is fully ready)
 * - isReady: boolean - true when both Clerk and Supabase token are initialized
 */
export function useAuthReady() {
  const { user, isLoaded: clerkLoaded } = useUser();
  const { isLoading: supabaseLoading, hasJwtTemplate } = useSupabase();
  const [tokenReady, setTokenReady] = useState(() => getAuthToken() !== null);

  // Poll for token availability when loading completes but token isn't set yet
  useEffect(() => {
    if (supabaseLoading || !clerkLoaded || !user?.id) return;

    // Check immediately
    const token = getAuthToken();
    if (token) {
      setTokenReady(true);
      return;
    }

    // If no JWT template configured, mark as ready (will use anon)
    if (!hasJwtTemplate) {
      setTokenReady(true);
      return;
    }

    // Token should be set soon - poll briefly to catch the async gap
    let attempts = 0;
    const maxAttempts = 20; // 2 seconds max
    const interval = setInterval(() => {
      attempts++;
      if (getAuthToken() !== null) {
        setTokenReady(true);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        // Give up - something is wrong, let it proceed anyway
        setTokenReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [supabaseLoading, clerkLoaded, user?.id, hasJwtTemplate]);

  // Reset when user signs out
  useEffect(() => {
    if (clerkLoaded && !user?.id) {
      setTokenReady(false);
    }
  }, [clerkLoaded, user?.id]);

  // Auth is ready when Clerk is loaded, Supabase finished loading, AND token is actually set
  const isReady = clerkLoaded && !supabaseLoading && !!user?.id && tokenReady;

  return {
    userId: isReady ? user.id : undefined,
    isReady,
  };
}

export default useAuthReady;
